import { useStores } from '@littlebird/sdk';
import { useParams } from '@tanstack/react-router';
import { observer } from 'mobx-react-lite';

import { Icons } from '@/assets/icons/icons';
import { Button } from '@/components/ui';
import { Tooltip } from '@/components/ui/Tooltip';
import { AgentModeLabel } from '@/features/agent-picker';
import { useCurrentChatMode } from '@/features/chat/chat.hooks';
import { useProactiveAgentsQuery } from '@/features/settings/proactive-agent.query';
import { trackEvent } from '@/utils/analytics';

import { ModeDropdown } from './chat-mode-dropdown';

const ChatModeDropdown = observer(() => {
  const { currentChatMode, setCurrentChatMode } = useCurrentChatMode();
  const { threads: threadStore } = useStores();
  const { chatId } = useParams({ strict: false });
  const thread = threadStore.getById(chatId ?? '');
  const agents = useProactiveAgentsQuery();
  // A proactive agent's chat always uses that agent, and the server ignores the mode picker there
  const owner = agents.data?.instances.find(
    (instance) => instance.chat_id === chatId
  );
  const handleChange = (modes: string[]) => {
    setCurrentChatMode(modes[0]);
    trackEvent('CHAT_MODEL_CHANGED', {
      selectedMode: modes[0],
      chatId: thread?.id,
    });
  };

  if (owner)
    return (
      <Tooltip
        label={`This chat belongs to the proactive agent ${owner.agent}. Its agent cannot be changed.`}
        side="bottom"
      >
        <span>
          <Button
            size="dense"
            variant="secondary"
            className="max-w-[22rem] gap-2 text-xs"
            disabled
          >
            <AgentModeLabel mode={owner.agent} />
          </Button>
        </span>
      </Tooltip>
    );

  return (
    <ModeDropdown.Root
      selectedModes={[currentChatMode]}
      setSelectedModes={handleChange}
    >
      <ModeDropdown.Trigger
        size="dense"
        className="max-w-[22rem] justify-between"
      >
        <AgentModeLabel mode={currentChatMode} />
        <Icons.chevronDown className="size-2 shrink-0" />
      </ModeDropdown.Trigger>
      <ModeDropdown.Content align="end" />
    </ModeDropdown.Root>
  );
});

export { ChatModeDropdown };
